// cache — the last good answer to /api/field/today and /api/field/job/:id,
// so a phone with no signal still shows this morning's list.
//
// localStorage under `fw_field_cache:<name>`, each entry { at, body }. Only a
// NetworkError falls back to it: a refusal from the server (404, 403, 500)
// is an answer and is shown as one. The UI marks cached data stale with the
// time it was fetched ("as of 7:42 AM") — never presents it as live.

import { isUnreachable } from '../../shared/http';

export const CACHE_PREFIX = 'fw_field_cache:';

function storage(override) {
  if (override) return override;
  try { return window.localStorage; } catch (_) { return null; }
}

export function readCache(name, store) {
  const s = storage(store);
  if (!s) return null;
  try {
    const raw = s.getItem(CACHE_PREFIX + name);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || !parsed.body) return null;
    return { at: parsed.at, body: parsed.body };
  } catch (_) { return null; }
}

export function writeCache(name, body, { store, now = () => new Date() } = {}) {
  const s = storage(store);
  if (!s || !body) return;
  try { s.setItem(CACHE_PREFIX + name, JSON.stringify({ at: now().toISOString(), body })); } catch (_) { /* full */ }
}

export function clearCache(name, store) {
  const s = storage(store);
  if (!s) return;
  try { s.removeItem(CACHE_PREFIX + name); } catch (_) { /* */ }
}

/**
 * Run `fetcher`; on success remember the body and return it with
 * `__stale: null`; on NetworkError return the cached body with `__stale` set
 * to when it was fetched; with no cache, rethrow so the screen says so.
 */
export async function loadWithCache(name, fetcher, opts = {}) {
  try {
    const body = await fetcher();
    writeCache(name, body, opts);
    return { ...body, __stale: null };
  } catch (e) {
    if (!isUnreachable(e)) throw e;
    const cached = readCache(name, opts.store);
    if (!cached) throw e;
    return { ...cached.body, __stale: cached.at };
  }
}
